import React, { useState, useEffect, useRef } from 'react';
import PersonIcon from '@mui/icons-material/Person';
import { CallEnd, Dialpad, Pause, PlayArrow } from '@mui/icons-material';
import { SvgIcon } from '@mui/material';
import DTMF from './Dtmf';
import { searchContacts } from '../services/webRTCService';
import styled from '@emotion/styled';

const CallDesign = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    position: fixed;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    background-color: #000;
    padding-top: 2rem;
    z-index: 100;
    span {
        font-family: 'Roboto', sans-serif;
        color: #fafafa;
    }
    .callTimer {
        font-size: 16px;
        line-height: 24px;
    }
    .callName {
        font-size: 24px;
        font-weight: 900;
        line-height: 22px;
        margin-top: 0.5rem;
    }
    .callNumber {
        font-size: 18px;
        font-weight: 400;
        line-height: 22px;
        margin-top: 1rem;
    }
`;

const ControlButtons = styled.div`
    position: absolute;
    bottom: 2rem;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 20px;
    .callButton {
        border: none;
        cursor: pointer;
        width: 70px;
        height: 70px;
        border-radius: 50%;
        svg {
            color: white;
            width: 24px;
            height: 24px;
        }
    }
`;

const IncomingCalls = ({
    session,
    auth,
    setCallInfo,
    rejectIncomingCall,
    toggleHold,
    selectedBnum,
}) => {
    const [incomingCallInfo, setIncomingCallInfo] = useState(null);
    const [callStatus, setCallStatus] = useState('');
    const [callTimer, setCallTimer] = useState(0);
    const [isCallOnHold, setIsCallOnHold] = useState(false);
    const rejectedCallRef = useRef(false);
    const [showDtmfKeypad, setShowDtmfKeypad] = useState(false);
    const [dtmfInput, setDtmfInput] = useState('');

    useEffect(() => {
        const fetchContactName = async (number) => {
            const name = await getContactName(number);
            setIncomingCallInfo({ number, name });
            setCallInfo({ name: name, number: number });
        };
        const handleFailedSession = (e) => {
            if (rejectedCallRef.current) return;
            // No session, so we send null
            rejectIncomingCall(null);
            console.error('Call failed:', e.cause);
        };

        const handleEndedSession = () => {
            if (rejectedCallRef.current) return;
            // No session, so we send null
            rejectIncomingCall(null);
            console.log('Call ended by the remote party.');
        };

        const handleHold = () => {
            console.log('Call put on hold');
            setIsCallOnHold(true);
        };

        const handleUnhold = () => {
            console.log('Call taken off hold');
            setIsCallOnHold(false);
        };

        if (session) {
            console.log('Remote stream available for session:', session);
            const uri = session.remote_identity.uri.toString();
            const number = uri.split('@')[0].replace('sip:', '');
            console.log('Incoming call from ******:', number);
            fetchContactName(number);

            session.on('failed', handleFailedSession);

            // Listen for the 'ended' event to detect when the call is hung up
            session.on('ended', handleEndedSession);
            session.on('hold', handleHold);
            session.on('unhold', handleUnhold);

            // const { from, to, callId } = session.remoteDescription.toJSON().sdp.split('a=identity:')[1].split('\r\n')[0].split(' ')[1];
            // setIncomingCallInfo({ from, to, callId });
        }
        // Cleanup function
        return () => {
            setIncomingCallInfo(null);
            setCallStatus('');
            setCallTimer(0);
            setIsCallOnHold(false);
            rejectedCallRef.current = false;
            if (session) {
                session.off('failed', handleFailedSession);
                session.off('ended', handleEndedSession);
                session.off('hold', handleHold);
                session.off('unhold', handleUnhold);
            }
            console.log('IncomingCalls cleanup - unmounted');
        };
    }, []);

    useEffect(() => {
        let interval;
        if (callStatus === 'active') {
            interval = setInterval(() => {
                setCallTimer((prevTimer) => prevTimer + 1);
            }, 1000);
        } else {
            setCallTimer(0);
        }

        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [callStatus]);

    const handleName = (contact) => {
        if (
            contact.firstNameLowerCase == null &&
            contact.lastNameLowerCase == null
        )
            return 'No name';
        if (
            contact.firstNameLowerCase == null &&
            contact.lastNameLowerCase != null
        )
            return `${contact.lastNameLowerCase.charAt(0).toUpperCase() + contact.lastNameLowerCase.slice(1)}`;
        if (
            contact.firstNameLowerCase != null &&
            contact.lastNameLowerCase == null
        )
            return `${contact.firstNameLowerCase.charAt(0).toUpperCase() + contact.firstNameLowerCase.slice(1)}`;
        return `${contact.firstNameLowerCase.charAt(0).toUpperCase() + contact.firstNameLowerCase.slice(1)} ${contact.lastNameLowerCase.charAt(0).toUpperCase() + contact.lastNameLowerCase.slice(1)}`;
    };

    const getContactName = async (number) => {
        try {
            const data = await searchContacts(
                auth,
                'search',
                number,
                100,
                false,
                selectedBnum?.country
            );

            // Assuming the API returns contact information
            // To handle the data accordingly
            console.log('Contact data:', data);

            const contactName =
                data.data.contacts.length === 1
                    ? handleName(data.data.contacts[0])
                    : '';
            return contactName;
        } catch (error) {
            console.error('Error fetching contact information:', error);
            // If there's an error, still proceed with the call using empty contact name
            return '';
        }
    };

    const acceptIncomingCall = () => {
        if (session) {
            console.log('Answering the call...');
            session.answer({
                mediaConstraints: { audio: true, video: false },
            });
            setCallStatus('active');
        }
        // hideIncomingCallModal();
    };

    const handleToggleHold = () => {
        if (toggleHold && session) {
            toggleHold(session);
        }
    };

    return (
        <>
            {callStatus === 'active' ? (
                <CallDesign>
                    {incomingCallInfo?.name ? (
                        <>
                            <span className="callName">
                                {incomingCallInfo.name}
                            </span>
                            <span className="callNumber">
                                {incomingCallInfo.number}
                            </span>
                        </>
                    ) : (
                        <>
                            <span className="callName">
                                {incomingCallInfo.number}
                            </span>
                        </>
                    )}
                    <SvgIcon sx={{ color: '#fafafa', fontSize: 100 }}>
                        <PersonIcon />
                    </SvgIcon>
                    <span className="callTimer" style={{ color: '#fafafa' }}>
                        {`${Math.floor(callTimer / 60)
                            .toString()
                            .padStart(
                                2,
                                '0'
                            )}:${(callTimer % 60).toString().padStart(2, '0')}`}
                    </span>
                    {isCallOnHold && (
                        <span
                            style={{
                                color: '#f39c12',
                                fontSize: '16px',
                                marginTop: '10px',
                            }}
                        >
                            (On Hold)
                        </span>
                    )}
                    <DTMF
                        callStatus={callStatus}
                        showDtmfKeypad={showDtmfKeypad}
                        session={session}
                        dtmfInput={dtmfInput}
                        setDtmfInput={(value) => setDtmfInput(value)}
                    />
                    <audio id="localAudio" autoPlay></audio>
                    <ControlButtons>
                        <button
                            onClick={() => {
                                rejectedCallRef.current = true;
                                rejectIncomingCall(session);
                            }}
                            className="callButton"
                            style={{ backgroundColor: '#e74c3c' }}
                        >
                            <CallEnd />
                        </button>
                        <button
                            onClick={handleToggleHold}
                            className="callButton"
                            style={{
                                backgroundColor: isCallOnHold
                                    ? '#f39c12'
                                    : '#444',
                                opacity: callStatus === 'active' ? 1 : 0.5,
                            }}
                        >
                            <SvgIcon>
                                {isCallOnHold ? (
                                    <PlayArrow
                                        sx={{ color: 'white', fontSize: 24 }}
                                    />
                                ) : (
                                    <Pause
                                        sx={{ color: 'white', fontSize: 24 }}
                                    />
                                )}
                            </SvgIcon>
                        </button>
                        {showDtmfKeypad ? (
                            <p
                                style={{
                                    color: '#fff',
                                    fontSize: '12px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                                onClick={() => setShowDtmfKeypad(false)}
                            >
                                Hide Keypad
                            </p>
                        ) : (
                            <button
                                onClick={() => setShowDtmfKeypad(true)}
                                className="callButton"
                                style={{
                                    backgroundColor: '#444',
                                }}
                            >
                                <SvgIcon>
                                    <Dialpad
                                        sx={{ color: 'white', fontSize: 24 }}
                                    />
                                </SvgIcon>
                            </button>
                        )}
                    </ControlButtons>
                </CallDesign>
            ) : (
                <>
                    <div
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            backgroundColor: 'rgba(0, 0, 0, 0.8)',
                            zIndex: 102,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '20px',
                        }}
                    >
                        <div
                            style={{
                                backgroundColor: '#fff',
                                borderRadius: '12px',
                                padding: '20px',
                                width: '90%',
                                maxWidth: '320px',
                                textAlign: 'center',
                            }}
                        >
                            <audio src="/ringtone.mp3" autoPlay loop></audio>
                            <>
                                <h3
                                    style={{
                                        margin: '0 0 10px',
                                        fontSize: '20px',
                                    }}
                                >
                                    Incoming Call
                                </h3>
                                <SvgIcon
                                    sx={{
                                        color: '#475467',
                                        fontSize: 64,
                                        margin: '10px 0',
                                    }}
                                >
                                    <PersonIcon />
                                </SvgIcon>
                                {incomingCallInfo?.name ? (
                                    <>
                                        <p
                                            style={{
                                                fontSize: '18px',
                                                fontWeight: 'bold',
                                                margin: '10px 0',
                                            }}
                                        >
                                            {incomingCallInfo?.name}
                                        </p>
                                        <p
                                            style={{
                                                fontSize: '16px',
                                                margin: '5px 0 20px',
                                            }}
                                        >
                                            {incomingCallInfo?.number}
                                        </p>
                                    </>
                                ) : (
                                    <p
                                        style={{
                                            fontSize: '18px',
                                            fontWeight: 'bold',
                                            margin: '10px 0',
                                        }}
                                    >
                                        {incomingCallInfo?.number}
                                    </p>
                                )}

                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-around',
                                    }}
                                >
                                    <button
                                        id="acceptCall"
                                        onClick={acceptIncomingCall}
                                        style={{
                                            backgroundColor: '#2ecc71',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '50%',
                                            width: '60px',
                                            height: '60px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                                        </svg>
                                    </button>
                                    <button
                                        id="rejectCall"
                                        onClick={() => {
                                            rejectedCallRef.current = true;
                                            rejectIncomingCall(session);
                                        }}
                                        style={{
                                            backgroundColor: '#e74c3c',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '50%',
                                            width: '60px',
                                            height: '60px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <CallEnd />
                                    </button>
                                </div>
                            </>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default IncomingCalls;
